import {
  DownloadSimple,
  Eye,
  FilePdf,
  Trash,
  VideoCamera,
} from "@phosphor-icons/react"
import { Carousel } from "@vesatogo/grass-carousel"
import { Image, Modal, NoDataFound, Spinner } from "@vesatogo/grass-core"
import { getExtension } from "@vesatogo/utils"
import { saveAs } from "file-saver"
import { useState } from "react"
import toast from "react-hot-toast"
import FileUploadButton from "~/components/FIleUploadButton"

type SendConsentType = {
  isOpen: boolean
  onClose: () => void
  form?: any
  disabled?: boolean
  addAttachment?: (attachment: any) => void
  attachments: any[]
  previewOnly?: boolean
}

const AttachmentModal = ({
  isOpen,
  onClose,
  form,
  disabled,
  addAttachment,
  attachments,
  previewOnly = false,
}: SendConsentType) => {
  const [loading, setLoading] = useState(false)

  const imageSlides = (attachments || []).map(
    (attachment: any, index: number) => {
      if (!attachment) return { slide: <div key={index} /> }
      const url =
        (attachment.media ? attachment.media.url : attachment.url) || ""
      const extension = getExtension(url)?.toLowerCase()
      const isPdf = extension === "pdf"
      const isVideo = ["mp4", "webm", "mov", "avi", "mkv"].includes(
        extension || ""
      )

      return {
        slide: (
          <div
            key={index}
            className="relative w-10 h-10 md:w-96 md:h-80 mx-auto rounded border p-2 flex items-center justify-center bg-gray-50"
          >
            {isPdf ? (
              <div className="flex flex-col items-center text-red-500">
                <FilePdf size={80} weight="fill" className="p-2" />
                <span className="text-sm uppercase font-bold mt-2">
                  PDF Document
                </span>
                <button
                  onClick={() => url && window.open(url, "_blank")}
                  className="mt-4 flex items-center gap-2 bg-green text-white px-4 py-2 rounded text-sm hover:bg-green-600 transition-colors"
                >
                  <Eye size={18} />
                  View PDF
                </button>
              </div>
            ) : isVideo ? (
              <div className="flex flex-col items-center text-blue-500">
                <VideoCamera size={80} weight="fill" className="p-2" />
                <span className="text-sm uppercase font-bold mt-2">
                  Video File
                </span>
                <button
                  onClick={() => url && window.open(url, "_blank")}
                  className="mt-4 flex items-center gap-2 bg-green text-white px-4 py-2 rounded text-sm hover:bg-green-600 transition-colors"
                >
                  <Eye size={18} />
                  View Video
                </button>
              </div>
            ) : (
              <Image
                src={url}
                alt={url}
                className="w-full h-full object-cover rounded"
              />
            )}
            {!attachment.id ? (
              <button
                onClick={() => {
                  const updatedAttachments = (attachments || []).filter(
                    (item, i) => i !== index
                  )
                  form?.setFieldValue("attachments", updatedAttachments)
                  addAttachment?.(updatedAttachments)
                }}
                disabled={disabled}
                className="absolute top-2 right-2 bg-white text-red p-1 m-2 rounded shadow-sm"
              >
                <Trash size={20} />
              </button>
            ) : (
              <div className="absolute top-2 right-2 bg-white text-green p-1 m-2 rounded cursor-pointer shadow-sm">
                <DownloadSimple
                  size={20}
                  onClick={() => {
                    if (url) {
                      saveAs(url, `${Date.now()}.${getExtension(url)}`)
                    }
                  }}
                />
              </div>
            )}
          </div>
        ),
      }
    }
  )

  const handleSuccess = message => {
    const media = message.map(item => ({
      media: item.data.media,
    }))
    const updatedAttachments = [...(attachments || []), ...media]
    form?.setFieldValue("attachments", updatedAttachments)
    addAttachment?.(updatedAttachments)
    onClose()
  }

  return (
    <>
      <Modal
        bodyClassName="md:w-1/2"
        title="Attachment"
        isOpen={isOpen}
        onClose={onClose}
      >
        {attachments.length === 0 ? (
          <NoDataFound
            title={"You haven’t added an Attachment"}
            children={
              loading ? (
                <Spinner />
              ) : (
                !previewOnly && (
                  <FileUploadButton
                    multiple
                    disabled={disabled}
                    method="put"
                    appName="setutrade"
                    environment={import.meta.env.VITE_APP_ENV}
                    endpoint={`https://${
                      import.meta.env.VITE_REST_URL
                    }/rest/trade/uploader/`}
                    media={attachments}
                    onSuccess={handleSuccess}
                    onError={message => {
                      toast.error(message)
                    }}
                    accept="image/*,application/pdf,video/*"
                    maxFileSize={10 * 1024 * 1024}
                  />
                )
              )
            }
          />
        ) : (
          <div>
            <div className="flex justify-end">
              {loading ? (
                <Spinner />
              ) : (
                !previewOnly && (
                  <FileUploadButton
                    multiple
                    disabled={disabled}
                    method="put"
                    appName="setutrade"
                    environment={import.meta.env.VITE_APP_ENV}
                    endpoint={`https://${
                      import.meta.env.VITE_REST_URL
                    }/rest/trade/uploader/`}
                    media={attachments}
                    onSuccess={handleSuccess}
                    onError={message => {
                      toast.error(message)
                    }}
                    accept="image/*,application/pdf,video/*"
                    maxFileSize={10 * 1024 * 1024}
                  />
                )
              )}
            </div>
            <Carousel
              className="mt-5"
              showArrows={true}
              transition={false}
              showDots={false}
              slides={imageSlides}
              options={{
                speed: 4,
                loop: true,
              }}
            />
          </div>
        )}
      </Modal>
    </>
  )
}

export default AttachmentModal
